import { ViperSelect } from "@/components";
import * as S from "@/components/styled";
import {
  flattenMilestones,
  updateMilestoneCompletionProperties,
} from "@/helpers";
import {
  IMilestoneSearchResult,
  IMilestoneTemplate,
  INotification,
} from "@/interfaces";
import { useProjectTemplateContext } from "@/providers";
import { useGetProjectTemplateQuery } from "@/services";
import { useGetAllFlattenedMilestoneTemplatesQuery } from "@/services/projectTemplate";
import { Modal, Row, Select, Space, Typography } from "antd";
import _ from "lodash";
import { useEffect, useState } from "react";
import { useIntl } from "react-intl";
import { toast } from "react-toastify";
import { v4 } from "uuid";
import { TemplateUniqueTextField } from "./ModalElements/TemplateUniqueTextField";
import {
  addChildMilestone,
  addMilestoneInList,
  doesDuplicateMilestoneValueExists,
} from "./helpers";

interface IProps {
  modalOpen: boolean;
  milestoneTemplates: IMilestoneTemplate[];
  targetMilestone: IMilestoneTemplate;
  saveMilestoneTemplates(milestoneTemplates: IMilestoneTemplate[]): void;
  onClose(): void;
  target: "child" | "before" | "after" | "top";
  expandedRows?: string[];
  setExpandedRows?: (value: string[]) => void;
  setMilestoneToExpand?: (value: string) => void;
}

export const MilestoneCreateModal = ({
  milestoneTemplates,
  targetMilestone,
  saveMilestoneTemplates,
  onClose,
  modalOpen,
  target,
  expandedRows = [],
  setExpandedRows,
  setMilestoneToExpand,
}: IProps) => {
  const intl = useIntl();
  const { Option } = Select;
  const { Text } = Typography;
  const [copyFromProjectTemplateId, setCopyFromProjectTemplateId] =
    useState<string>();
  const { data: copyFromProjectTemplate } = useGetProjectTemplateQuery(
    copyFromProjectTemplateId,
    {
      skip: !copyFromProjectTemplateId,
    }
  );
  const [isValid, setIsValid] = useState({});
  const [title, setTitle] = useState("");
  const [selectedMilestone, setSelectedMilestone] = useState<string>();
  const [searchValue, setSearchValue] = useState<string>();
  const [isDuplicate, setIsDuplicate] = useState(false);
  const [filteredData, setFilteredData] = useState<IMilestoneSearchResult[]>(
    []
  );
  const { data: projectMilestones } =
    useGetAllFlattenedMilestoneTemplatesQuery();

  const { shouldCompleteProjectMilestoneTemplate } =
    useProjectTemplateContext();

  const newNotification: INotification = {
    roles: [],
    groups: [],
    coreTeamGroups: [],
  };

  const newMilestoneTemplate: IMilestoneTemplate = {
    id: v4(),
    childMilestones: [],
    approvers: [],
    projectLeaderApprovers: [],
    projectLeaderNotifications: [],
    targetDatePrepopulationSource: "",
    defaultFor: [],
    canBeAdded: [],
    canBeDeleted: [],
    canBeSkipped: [],
    approvalRequired: [],
    milestoneTemplateId: "",
    name: "",
    abbreviation: "",
    distributionsRequired: false,
    documentsRequired: false,
    targetDatesRequired: false,
    areProjectSkusRequired: false,
    notification: newNotification,
    isActive: false,
    shouldCompleteProject: false,
    secureProjectOnCompletion: false,
    approvalRequiredCheckboxSelectedAll: false,
    approvalRequiredCheckboxSelectedNone: false,
    canBeAddedCheckboxSelectedAll: false,
    canBeAddedCheckboxSelectedNone: false,
    canBeDeletedCheckboxSelectedAll: false,
    canBeDeletedCheckboxSelectedNone: false,
    canBeSkippedCheckboxSelectedAll: false,
    canBeSkippedCheckboxSelectedNone: false,
    defaultForCheckboxSelectedAll: false,
    defaultForCheckboxSelectedNone: false,
  };

  const [milestoneTemplate, setMilestoneTemplate] =
    useState<IMilestoneTemplate>(newMilestoneTemplate);

  useEffect(() => {
    if (target === "child") {
      setTitle(
        intl.formatMessage(
          {
            id: "milestoneTemplates.addModalChild",
          },
          {
            target,
            name: targetMilestone.name,
          }
        )
      );
    } else if (target === "top") {
      setTitle(
        intl.formatMessage(
          {
            id: "milestoneTemplates.addMilestoneTop",
          },
          {
            target,
            name: targetMilestone.name,
          }
        )
      );
    } else {
      setTitle(
        intl.formatMessage(
          {
            id: "milestoneTemplates.addModalLocation",
          },
          {
            target,
            name: targetMilestone.name,
          }
        )
      );
    }
  }, [targetMilestone, target, intl]);

  useEffect(() => {
    if (!selectedMilestone) return;
    setSearchValue(selectedMilestone);
    const copyFrom = filteredData?.find(
      (milestone) => milestone.id === selectedMilestone
    );
    setCopyFromProjectTemplateId(copyFrom?.projectTemplateId);
  }, [selectedMilestone, filteredData]);

  useEffect(() => {
    if (!searchValue) return;
    if (searchValue.startsWith(" ")) return;

    setMilestoneTemplate({
      ...milestoneTemplate,
      name: searchValue,
    });

    const duplicateExists = doesDuplicateMilestoneValueExists(
      milestoneTemplates,
      "name",
      searchValue,
      milestoneTemplate?.id
    );

    setIsDuplicate(duplicateExists);
  }, [searchValue]);

  const setQuery = (query: string): void => {
    setSearchValue(query);
    if (projectMilestones) {
      const filtered = projectMilestones.filter(
        (milestone) =>
          milestone.name.toLowerCase().includes(query.toLowerCase()) ||
          milestone.abbreviation.toLowerCase().includes(query.toLowerCase())
      );
      const uniqeFiltered = filtered.filter(
        (milestone, index, self) =>
          self.findIndex((obj) => obj.id === milestone.id) === index
      );
      const mappedUniqueFiltered = uniqeFiltered.map(
        (milestone: IMilestoneSearchResult) => ({
          name: milestone.name,
          id: milestone.id,
          abbreviation: milestone.abbreviation,
          projectTemplateId: milestone.projectTemplateId,
          projectTypeName: milestone.projectTypeName,
        })
      );
      if (mappedUniqueFiltered !== null) {
        setFilteredData(mappedUniqueFiltered);
      }
    }
  };

  const handleOnClose = () => {
    setSearchValue("");
    setSelectedMilestone("");
    setFilteredData([]);
    setIsValid({});
    setIsDuplicate(false);
    setMilestoneTemplate(newMilestoneTemplate);

    onClose();
  };

  const handleSave = (): void => {
    let updatedMilestoneTemplates = _.cloneDeep(milestoneTemplates);

    updateMilestoneCompletionProperties(
      updatedMilestoneTemplates,
      shouldCompleteProjectMilestoneTemplate
    );

    let newMilestone: IMilestoneTemplate;
    if (selectedMilestone && projectMilestones) {
      if (copyFromProjectTemplate) {
        const flattenedMilestones = flattenMilestones(
          copyFromProjectTemplate.milestones
        );
        newMilestone = flattenedMilestones.find(
          (milestone) => milestone.id === selectedMilestone
        );
        newMilestone.id = milestoneTemplate.id;
      } else {
        toast.error(
          intl.formatMessage({ id: "general.toastSomethingWentWrong" })
        );
        return;
      }
    } else {
      newMilestone = milestoneTemplate;
    }

    switch (target) {
      case "child": {
        addChildMilestone(
          updatedMilestoneTemplates,
          targetMilestone,
          newMilestone,
          intl
        );
        if (setExpandedRows && setMilestoneToExpand) {
          //these should always be defined here, forced the check to appease Typescript.
          setExpandedRows([...expandedRows, targetMilestone.id]);
          setMilestoneToExpand(newMilestone.id);
        }
        break;
      }
      case "top": {
        updatedMilestoneTemplates = [
          newMilestone,
          ...updatedMilestoneTemplates,
        ];
        break;
      }
      case "after": {
        addMilestoneInList(
          updatedMilestoneTemplates,
          targetMilestone,
          newMilestone,
          target
        );
        break;
      }
      case "before": {
        addMilestoneInList(
          updatedMilestoneTemplates,
          targetMilestone,
          newMilestone,
          target
        );
        break;
      }
      default:
        console.error(`${target} action not defined.`);
        break;
    }
    setMilestoneTemplate(newMilestoneTemplate);
    saveMilestoneTemplates(updatedMilestoneTemplates);
    handleOnClose();
  };

  const searchResults = filteredData?.map(
    ({ id, abbreviation, name, projectTypeName }) => (
      <Option key={id} label={name} value={id}>
        <Space direction="vertical" size={0}>
          <Text className="font-bold">{name}</Text>
          {!!abbreviation && (
            <Text type="secondary">
              <small>
                {intl.formatMessage({
                  id: "milestoneTemplates.colAbbreviation",
                })}
                : {abbreviation}
              </small>
            </Text>
          )}
          {!!projectTypeName && (
            <Text type="secondary">
              <small>
                {intl.formatMessage({
                  id: "projectTemplate.colProjectType",
                })}
                : {projectTypeName}
              </small>
            </Text>
          )}
        </Space>
      </Option>
    )
  );

  if (!modalOpen) return null;

  return (
    <Modal
      title={title}
      centered
      open={modalOpen}
      onCancel={handleOnClose}
      onOk={handleSave}
      destroyOnHidden
      okButtonProps={{
        disabled: selectedMilestone
          ? false
          : !(
              Object.values(isValid).some((valid) => valid) &&
              searchValue &&
              !isDuplicate
            ),
      }}
      okText={intl.formatMessage({
        id: "general.save",
      })}
    >
      <Row>
        Name
        <ViperSelect
          data-test-id={`copy-milestone-select`}
          onChange={setSelectedMilestone}
          customOptions={searchResults}
          allowMultipleSelections={false}
          onFetchSuggestions={setQuery}
          placeholder={intl.formatMessage({ id: "general.search" })}
          loading={false}
          optionLabelProp="label"
          value={searchValue}
        />
        {isDuplicate && (
          <S.StyledTag
            data-test-id={`name-and-abbreviation-editor-dup-name-error-label`}
          >
            {intl.formatMessage({
              id: `milestoneTemplates.nameDupError`,
            })}
          </S.StyledTag>
        )}
      </Row>
      <Row>
        <TemplateUniqueTextField
          milestoneTemplate={milestoneTemplate}
          projectMilestoneTemplates={milestoneTemplates}
          propertyName={"abbreviation"}
          setProjectMilestoneTemplate={setMilestoneTemplate}
          setIsValid={setIsValid}
          selectedValue={
            filteredData.find((x) => x.id === selectedMilestone)
              ?.abbreviation || ""
          }
          isDisabled={selectedMilestone ? true : false}
        />
      </Row>
    </Modal>
  );
};
